import { guardMediaDownloads } from "../../utils/media-download-guard";
import { showWatermark } from "../../utils/watermark";
import { Button, buttonVariant, ToastContext, ToastType } from "@humansignal/ui";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { generatePath, useHistory } from "react-router";
import { modal } from "../../components/Modal/Modal";
import * as extensions from "@afriannotate/extensions";
import { Space } from "../../components/Space/Space";
import { useAPI } from "../../providers/ApiProvider";
import { useProject } from "../../providers/ProjectProvider";
import { ProjectNav } from "../ProjectDashboard/ProjectNav";
import { ConsentGate } from "../../components/ConsentGate/ConsentGate";
import { SpeakerGate } from "../../components/SpeakerGate/SpeakerGate";
import { RateChangeGate } from "../../components/RateChangeGate/RateChangeGate";
import { ProjectLocaleScope } from "../../i18n/ProjectLocaleScope";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { useUpdatePageTitle, createTitleFromSegments, instructionsToHtml } from "@humansignal/core";
import { useContextProps, useParams } from "../../providers/RoutesProvider";
import { addAction, addCrumb, deleteAction, deleteCrumb } from "../../services/breadrumbs";
import { cn } from "../../utils/bem";
import { isDefined } from "../../utils/helpers";
import { ImportModal } from "../CreateProject/Import/ImportModal";
import { ExportPage } from "../ExportPage/ExportPage";
import { ProjectMetricsModal } from "../Settings/ProjectMetricsModal";
import { APIConfig } from "./api-config";
import { useTranslation } from "../../i18n/LocaleProvider";
import { isCloudReachable, useOnlineStatus } from "../../hooks/useOnlineStatus";
import { showOfflineModal } from "../../components/OfflineCard/OfflineCard";
import { authHeaders } from "../../utils/jwt-storage";
import { MediaSplitter } from "../../components/MediaSplitter/MediaSplitter";
import { dmPanels as extensionPanels } from "@afriannotate/extensions";

import "./DataManager.prefix.css";

const loadDependencies = () => [import("@humansignal/datamanager"), import("@humansignal/editor")];

const initializeDataManager = async (root, props, params) => {
  if (!window.LabelStudio) throw Error("Label Studio Frontend doesn't exist on the page");
  if (!root || root.dataset.dmInitialized) return;

  root.dataset.dmInitialized = true;

  const { ...settings } = root.dataset;
  const canManage = !!params.canManageProject;

  const dmConfig = {
    root,
    projectId: params.id,
    apiGateway: `${window.APP_SETTINGS.hostname}/api/dm`,
    apiVersion: 2,
    project: params.project,
    polling: window.APP_SETTINGS?.polling,
    showPreviews: false,
    apiEndpoints: APIConfig.endpoints,
    // JWT Bearer for the Capacitor mobile bundle (and any web /
    // desktop session that authenticates via JWT instead of the
    // Django session cookie). ``authHeaders()`` returns ``{}`` when
    // no JWT is present so the cookie-session path keeps working
    // unchanged. Without this, the DM SDK creates its own APIProxy
    // with no auth and every ``/api/dm/columns?project=N`` call
    // returns 401 from the cloud — surfaces in the LSF shell as
    // "LS API not available; check API_GATEWAY and LS_ACCESS_TOKEN".
    apiHeaders: authHeaders(),
    interfaces: {
      import: canManage,
      export: canManage,
      // Analytics lives on the Project → Analytics dashboard tab (ProjectNav),
      // which already carries both Metrics and the Review Queue. Dropping the
      // duplicate Data Manager toolbar button keeps the DM toolbar slim and
      // gives Analytics a single home.
      metrics: false,
      // Audio / video splitter: managers only, and only when the label
      // config has a media field to cut.
      split: canManage && params.hasMedia,
      backButton: false,
      labelingHeader: false,
      autoAnnotation: params.autoAnnotation,
    },
    labelStudio: {
      keymap: window.APP_SETTINGS.editor_keymap,
    },
    ...props,
    ...settings,
  };

  return new window.DataManager(dmConfig);
};

const buildLink = (path, params) => {
  return generatePath(`/projects/:id${path}`, params);
};

export const DataManagerPage = ({ ...props }) => {
  const { t } = useTranslation();
  const dependencies = useMemo(loadDependencies, []);
  const toast = useContext(ToastContext);
  const root = useRef();
  // The DM mount node lives behind gates (ConsentGate / SpeakerGate /
  // ProjectLocaleScope) that render ``null`` while their checks are
  // pending, so the ``<div ref={root}>`` can mount LATER than the init
  // effect first runs. A plain ref doesn't trigger a re-render when the
  // node attaches, so init() would bail on ``!root.current`` and never
  // re-run — the intermittent "blank Data Manager until refresh". This
  // callback ref flips ``rootReady`` when the node mounts/unmounts so the
  // init effect below re-runs the moment the div is actually in the DOM.
  const [rootReady, setRootReady] = useState(false);
  const attachRoot = useCallback((node) => {
    root.current = node ?? undefined;
    setRootReady(!!node);
  }, []);
  const params = useParams();
  const history = useHistory();
  const api = useAPI();
  const { project } = useProject();
  // A project may ask for the browser's download and save menus to be
  // taken off its media (``project.extensions.no_media_download``, set by
  // an installed extension). A deterrent: see utils/media-download-guard.
  const guarded = !!project?.extensions?.no_media_download;
  useEffect(() => (guarded ? guardMediaDownloads(document) : undefined), [guarded]);
  const { user } = useAuth();
  // And for the viewer's name and the time to be laid faintly across its
  // pages (``project.extensions.watermark``): see utils/watermark.
  const marked = !!project?.extensions?.watermark;
  const viewer = [[user?.first_name, user?.last_name].filter(Boolean).join(" "), user?.email]
    .filter(Boolean)
    .join(", ");
  useEffect(() => (marked && viewer ? showWatermark(viewer) : undefined), [marked, viewer]);
  useUpdatePageTitle(createTitleFromSegments([project?.title, "Data Manager"]));
  const setContextProps = useContextProps();
  const [crashed, setCrashed] = useState(false);
  // Review-lock banner: when the opened annotation carries a verdict,
  // say so ABOVE the editor — the locked (no-Update) state used to be
  // a silent mystery to annotators. Populated from onSelectAnnotation.
  const [reviewBanner, setReviewBanner] = useState(null);
  const [splitSource, setSplitSource] = useState(null);
  // Server-verified offline block for metadata-only projects. The
  // client-side probe flag races (it starts optimistic and may be
  // stale on the page we navigated FROM), so init() asks the local
  // Django's /api/afri/auth/cloud_reachable/ — authoritative, cached
  // server-side, ~10ms — before letting the DM initialize.
  const [probeBlocked, setProbeBlocked] = useState(false);
  const [loading, setLoading] = useState(!window.DataManager || !window.LabelStudio);
  const dataManagerRef = useRef();

  // Panels an installed extension opens beside the table, each on a Data
  // Manager event (a toolbar button its mode adds emits it).
  const [openPanel, setOpenPanel] = useState(null);
  const labelTask = useCallback((taskId) => {
    setOpenPanel(null);
    dataManagerRef.current?.store?.startLabeling?.({ id: taskId });
  }, []);
  const projectId = project?.id;

  // The project nav's "Data Manager" tab links to this same page. From a
  // task or the Label All stream that link only drops ?task= / ?labeling=
  // from the URL, and the DM (which tracks its own history state) stayed
  // on the task. Treat arriving at the bare task-list URL as "close
  // labeling", which is what the Data Manager breadcrumb already does.
  useEffect(() => {
    return history.listen((loc) => {
      if (!/^\/projects\/\d+\/data\/?$/.test(loc.pathname)) return;
      const q = new URLSearchParams(loc.search);
      if (q.has("task") || q.has("labeling")) return;
      const store = dataManagerRef.current?.store;
      if (store?.isLabeling) store.closeLabeling({ pushState: false });
    });
  }, [history]);
  const canManageProject =
    !!user &&
    (user.is_staff ||
      user.is_superuser ||
      ["OW", "AD", "MA"].includes(user.active_organization_role) ||
      project?.created_by?.id === user.id);

  const init = useCallback(async () => {
    if (!window.LabelStudio) return;
    if (!window.DataManager) return;
    if (!root.current) return;
    if (!project?.id) return;
    // The toolbar's import/export visibility (``canManageProject``) is
    // baked into the DM's interfaces at CONSTRUCTION time. Initializing
    // before the auth user has loaded computed it as false and locked
    // the buttons hidden until a full reload — the intermittent
    // "Import/Export missing" bug. Wait for the user; the deps below
    // re-run init the moment it arrives.
    if (!user) return;
    if (dataManagerRef.current) return;

    // Metadata-only project on the desktop: its task reads proxy to
    // the cloud, so confirm the cloud is actually reachable BEFORE
    // initializing — otherwise the DM's first grid fetch 503s and
    // crashes into "Runtime error / Service Unavailable" faster than
    // the client-side offline flag can flip. Ask the local server
    // (authoritative, ~10ms) instead of trusting the client flag.
    const desktopShell =
      typeof window !== "undefined" &&
      (window.location.hostname === "127.0.0.1" || window.location.hostname === "localhost") &&
      window.location.port === "17080";
    if (desktopShell && project?.local_sync_tier === "metadata_only") {
      try {
        const r = await fetch("/api/afri/auth/cloud_reachable/", {
          credentials: "same-origin",
          cache: "no-store",
        });
        const b = await r.json();
        if (!b?.reachable) {
          setProbeBlocked(true);
          return;
        }
      } catch (_) {
        setProbeBlocked(true);
        return;
      }
      setProbeBlocked(false);
    }

    const mlBackends = await api.callApi("mlBackends", {
      params: { project: project.id },
    });

    const interactiveBacked = (mlBackends ?? []).find(({ is_interactive }) => is_interactive);

    const dataManager = (dataManagerRef.current =
      dataManagerRef.current ??
      (await initializeDataManager(root.current, props, {
        ...params,
        project,
        autoAnnotation: isDefined(interactiveBacked),
        canManageProject,
        // The splitter only makes sense when the label config has an
        // audio / video field (the clips need somewhere to go).
        // The parsed config lists control tags; the media object tags
        // sit under each control's ``inputs``.
        hasMedia: Object.values(project?.parsed_label_config ?? {}).some(
          (tag) =>
            ["Audio", "AudioPlus", "Video"].includes(tag?.type) ||
            (tag?.inputs ?? []).some((input) => ["Audio", "AudioPlus", "Video"].includes(input?.type)),
        ),
      })));

    Object.assign(window, { dataManager });

    dataManager.on("crash", (details) => {
      const error = details?.error;
      const isMissingTaskError = error?.startsWith("Task ID:");
      const isMissingProjectError = error?.startsWith("Project ID:");

      if (isMissingTaskError || isMissingProjectError) {
        const message = `The ${
          isMissingTaskError ? "task" : "project"
        } you are trying to access does not exist or is no longer available.`;

        toast.show({
          message,
          type: ToastType.error,
          duration: 10000,
        });
      }

      if (isMissingTaskError) {
        history.push(buildLink("", { id: params?.id ?? project?.id }));
      } else if (isMissingProjectError) {
        history.push("/projects");
      }
    });

    dataManager.on("settingsClicked", () => {
      history.push(buildLink("/settings/labeling", { id: params?.id ?? project?.id }));
    });

    dataManager.on("importClicked", () => {
      history.push(buildLink("/data/import", { id: params?.id ?? project?.id }));
    });

    // Navigate to Storage Settings and auto-open Add Source Storage modal
    dataManager.on("openSourceStorageModal", () => {
      history.push(buildLink("/settings/storage?open=source", { id: params?.id ?? project?.id }));
    });

    dataManager.on("exportClicked", () => {
      // Carry the current Data Manager tab so Export (and its Preview)
      // scope to exactly what the user is looking at. Unsaved tabs have
      // a non-numeric key — those fall back to the whole project.
      const view = dataManager.store?.currentView;
      const viewId = view && /^\d+$/.test(String(view.tabKey ?? view.id ?? "")) ? String(view.tabKey ?? view.id) : null;
      const base = buildLink("/data/export", { id: params?.id ?? project?.id });
      history.push(viewId ? `${base}?view=${viewId}` : base);
    });

    for (const panel of extensionPanels ?? []) {
      dataManager.on(panel.event, () => setOpenPanel((v) => (v === panel.id ? null : panel.id)));
    }

    dataManager.on("metricsClicked", () => {
      history.push(buildLink("/data/metrics", { id: params?.id ?? project?.id }));
    });

    // Split media: open the splitter for the one selected task; the
    // Data Manager reloads its list when clips were added or the source
    // was removed.
    dataManager.on("splitMediaClicked", ({ taskId } = {}) => {
      if (taskId != null) setSplitSource({ taskId });
    });

    dataManager.on("error", (response) => {
      api.handleError(response);
    });

    // Friendly end-of-queue handler. Fires when the labeling stream
    // runs out of tasks for the current user (backend returns 404 or
    // 403 for next_task — see AppStore.apiCall / tasks.loadNextTask
    // for where 4xx is reinterpreted as end-of-stream). Show a
    // positive completion toast and, once it's had time to register,
    // call the store's ``closeLabeling`` which unsets the task, flips
    // the DM back to explorer mode, destroys the LSF instance, and
    // navigates the URL back to the DM tab. Avoids leaving the user
    // on the bare "You're all caught up" editor screen.
    dataManager.on("onSelectAnnotation", async (...args) => {
      try {
        // Signature varies across DM versions — hunt the annotation pk.
        const flat = args.flat ? args.flat(2) : args;
        const pk = flat.map((a) => a && (a.pk || a.id)).find((v) => Number.isInteger(Number(v)) && Number(v) > 0);
        if (!pk) {
          setReviewBanner(null);
          return;
        }
        const res = await api.callApi("annotation", { params: { pk: Number(pk) }, suppressError: true });
        if (res && !res.error && res.review_state) {
          const highlighted = !!(
            res.review_diff &&
            ((res.review_diff.changed || []).length || (res.review_diff.added || []).length)
          );
          setReviewBanner({
            state: res.review_state,
            summary: res.review_diff?.summary || null,
            comment: res.review_comment || null,
            highlighted,
          });
          // Review verdict as a transient toast (no in-page banner):
          // title, the reviewer's summary/comment, and the lock hint.
          const title =
            res.review_state === "fixed"
              ? "Fixed and accepted in review"
              : res.review_state === "accepted"
                ? "Accepted in review"
                : "Rejected in review";
          const parts = [title];
          if (res.review_diff?.summary) parts.push(res.review_diff.summary);
          if (res.review_comment) parts.push(`"${res.review_comment}"`);
          if (res.review_state !== "rejected") {
            parts.push(
              highlighted
                ? "Highlighted regions were changed in review. Accepted work is locked; changes go through the review stream."
                : "Accepted work is locked; changes go through the review stream.",
            );
          }
          toast.show({
            id: "review-lock",
            message: parts.join(". "),
            type: res.review_state === "rejected" ? "error" : "info",
            duration: 8000,
          });
          // Phase 2: paint the reviewer's edits ON the canvas — every
          // changed/added region gets the editor's highlight outline,
          // so "what was fixed" is visible in place, not just as chips.
          const ids = [
            ...(res.review_diff?.changed || []).map((c) => c.id),
            ...(res.review_diff?.added || []).map((c) => c.id),
          ].filter(Boolean);
          if (ids.length) {
            const tryHighlight = (attempt = 0) => {
              try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const ann = window.Htx?.annotationStore?.selected;
                const regions = ann?.regionStore?.regions ?? [];
                if (!regions.length && attempt < 10) {
                  window.setTimeout(() => tryHighlight(attempt + 1), 600);
                  return;
                }
                for (const region of regions) {
                  const rid = region.id ?? region.cleanId;
                  const match = ids.some(
                    (id) =>
                      rid === id ||
                      String(rid).startsWith(`${id}#`) ||
                      (region.results ?? []).some((x) => x.pid === id || x.id === id),
                  );
                  if (match) region.setHighlight?.(true);
                }
              } catch (_) {
                /* highlight is best-effort */
              }
            };
            tryHighlight();
          }
        } else {
          setReviewBanner(null);
        }
      } catch (_) {
        setReviewBanner(null);
      }
    });
    dataManager.on("closeTask", () => {
      setReviewBanner(null);
      toast.dismiss?.("review-lock");
    });

    dataManager.on("labelStreamFinished", () => {
      const id = `label-stream-finished-${params?.id ?? project?.id}`;
      toast.show({
        message: t("pages.datamanager.data_manager.youre_all_caught_up_no_more_tasks_availa"),
        type: ToastType.info,
        id,
        duration: 3500,
      });
      setTimeout(() => {
        dataManager.store?.closeLabeling?.();
      }, 3500);
    });

    dataManager.on("toast", ({ message, type, id, duration }) => {
      toast.show({ message, type, id, duration });
    });

    dataManager.on("toast:dismiss", ({ id } = {}) => {
      toast.dismiss(id);
    });

    dataManager.on("navigate", (route) => {
      const target = route.replace(/^projects/, "");

      if (target) history.push(buildLink(target, { id: params?.id ?? project?.id }));
      else history.push("/projects");
    });

    if (interactiveBacked) {
      dataManager.on("lsf:regionFinishedDrawing", (reg, group) => {
        const { lsf, task, currentAnnotation: annotation } = dataManager.lsf;
        const ids = group.map((r) => r.cleanId);
        const result = annotation.serializeAnnotation().filter((res) => ids.includes(res.id));

        const suggestionsRequest = api.callApi("mlInteractive", {
          params: { pk: interactiveBacked.id },
          body: {
            task: task.id,
            context: { result },
          },
        });

        // we'll check that we are processing the same task
        const wrappedRequest = new Promise(async (resolve, reject) => {
          const response = await suggestionsRequest;

          // right now task might be an old task,
          // so in order to get a current one we need to get it from lsf
          if (task.id === dataManager.lsf.task.id) {
            resolve(response);
          } else {
            reject();
          }
        });

        lsf.loadSuggestions(wrappedRequest, (response) => {
          if (response.data) {
            return response.data.result;
          }

          return null;
        });
      });
    }

    setContextProps({ dmRef: dataManager });
    // ``user`` / ``canManageProject`` in the deps: init re-runs when auth
    // resolves (see the early return above) — projectId alone left the
    // callback with a stale null user forever.
  }, [projectId, user, canManageProject]);

  const destroyDM = useCallback(() => {
    if (dataManagerRef.current) {
      dataManagerRef.current.destroy();
      dataManagerRef.current = null;
    }
  }, []);

  // Desktop offline gate for not-downloaded projects. A metadata-only
  // project holds no local task rows — its Data Manager reads proxy to
  // the cloud (CloudProxyMiddleware), which only works online. When
  // the cloud is unreachable, rendering the DM produced the infinite
  // "Loading project…" hang / a misleading empty Import screen; block
  // with an explanation instead. ``useOnlineStatus`` is the same probe
  // driving the menubar pill, so the two surfaces agree.
  // ``useOnlineStatus`` starts optimistically ONLINE until its first
  // probe (~1s) — long enough for the DM to initialize, fire its task
  // fetch, get the 503, and crash with "Runtime error / Service
  // Unavailable" before the flag flips. ``isCloudReachable`` reads the
  // globally-maintained probe flag, which in an already-offline
  // session is ALREADY false at mount — AND-ing the two closes the
  // race window for the common "app knows it's offline" case.
  const cloudUp = useOnlineStatus() && isCloudReachable();
  const isDesktopShell =
    typeof window !== "undefined" &&
    (window.location.hostname === "127.0.0.1" || window.location.hostname === "localhost") &&
    window.location.port === "17080";
  const offlineBlocked = probeBlocked || (isDesktopShell && !cloudUp && project?.local_sync_tier === "metadata_only");

  // Self-heal: while blocked, poll the LOCAL server's authoritative
  // cloud probe and unblock when the cloud answers again (the init
  // effect then re-runs and mounts the real DM). Deliberately NOT
  // keyed on ``cloudUp``: useOnlineStatus probes /api/version/ on the
  // LOCAL Django, which is alive even when the cloud is gone — keying
  // the unblock on it looped block→unblock→block forever and rendered
  // as a faded, endlessly-reloading page.
  useEffect(() => {
    if (!probeBlocked) return undefined;
    let alive = true;
    const id = setInterval(async () => {
      try {
        const r = await fetch("/api/afri/auth/cloud_reachable/", {
          credentials: "same-origin",
          cache: "no-store",
        });
        const b = await r.json();
        if (alive && b?.reachable) setProbeBlocked(false);
      } catch (_) {
        /* still down — keep waiting */
      }
    }, 10000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [probeBlocked]);

  // When the block engages: fire the global offline modal and BOUNCE
  // BACK to the page the user came from — landing on an empty Data
  // Manager behind the modal (and staying there after Close) read as
  // a broken page. The probe resolves in ~10-50ms, so the bounce is
  // effectively "the click didn't navigate, it explained itself".
  useEffect(() => {
    if (!offlineBlocked) return;
    showOfflineModal({ kind: "project" });
    if (window.history.length > 1) history.goBack();
    else history.replace("/projects");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offlineBlocked]);

  useEffect(() => {
    // ``offlineBlocked`` in the deps: while blocked the DM root div
    // isn't rendered, so init() bails on !root.current. When the
    // connection comes back the flag flips, the root mounts, and this
    // effect must re-run to actually initialize the DM.
    // ``rootReady`` likewise: gates above (SpeakerGate etc.) can mount
    // the root div AFTER this effect first runs, so re-run when it
    // attaches — otherwise init() bails on a null root and never retries.
    if (offlineBlocked) return;
    if (!rootReady) return;
    Promise.all(dependencies)
      .then(() => setLoading(false))
      .then(init);
  }, [init, offlineBlocked, rootReady]);

  useEffect(() => {
    // destroy the data manager when the component is unmounted
    return () => destroyDM();
  }, []);

  if (offlineBlocked) {
    // Momentary empty frame while the bounce-back navigation (effect
    // above) unmounts this page — the user ends up back where they
    // clicked, with the global offline modal explaining.
    return <div className={cn("datamanager").toClassName()} />;
  }

  return crashed ? (
    <div className={cn("crash").toClassName()}>
      <div className={cn("crash").elem("info").toClassName()}>
        {t("pages.datamanager.data_manager.project_was_deleted_or_not_yet_created")}
      </div>

      <Button
        onClick={() => {
          window.location.href = "/projects";
        }}
        aria-label={t("pages.datamanager.data_manager.back_to_projects")}
      >
        {t("pages.datamanager.data_manager.back_to_projects")}
      </Button>
    </div>
  ) : (
    // ConsentGate sits OUTSIDE the DM root mount so the labeling UI
    // doesn't initialize before unsigned consents resolve. The gate
    // refetches /pending/ after every signature; once empty it falls
    // through to the children which then run ``init()`` against a
    // fresh ``root.current``. Server-side enforcement in
    // tasks/api.py:AnnotationsListAPI.perform_create + draft create
    // is the actual security boundary; this gate is the UX layer.
    //
    // ProjectLocaleScope sits inside the consent gate so the labeling
    // chrome — and the embedded LSF editor when the user goes into
    // labelstream mode — pick up Project.annotator_language. Empty
    // override (the default) is a no-op pass-through, so the data
    // manager grid keeps using the viewer's personal language for
    // projects that haven't opted in to per-project locale.
    <ConsentGate projectId={projectId}>
      <SpeakerGate project={project}>
        {/* Rate-change protection: blocking accept/exit modal when the
          owner changed the pay rates mid-campaign. */}
        <RateChangeGate projectId={projectId} />
        <ProjectLocaleScope project={project}>
          {/* No separate page-level loader here on purpose: the Data Manager
            renders its own single loading indicator (one logo, one position)
            once mounted, so we don't get two loaders flashing at different
            spots while the bundle and then the data load. */}
          {/* Allow this to exist before the DataManager is initialized as the async app.fetchData call eventually calls startLabeling, and that requires the root element to exist */}
          {/* Review verdict is shown as a toast (see onSelectAnnotation). */}
          <div ref={attachRoot} className={cn("datamanager").toClassName()} />
          {(extensionPanels ?? [])
            .filter((panel) => panel.id === openPanel)
            .map(({ id, component: Panel }) => (
              <Panel key={id} project={project} onLabelTask={labelTask} onClose={() => setOpenPanel(null)} />
            ))}
          {splitSource && projectId && (
            <MediaSplitter
              projectId={projectId}
              source={splitSource}
              onClose={() => setSplitSource(null)}
              onDone={() => {
                try {
                  dataManagerRef.current?.store?.currentView?.reload?.();
                } catch {
                  /* the next refresh shows the clips */
                }
              }}
            />
          )}
        </ProjectLocaleScope>
      </SpeakerGate>
    </ConsentGate>
  );
};

// ``title`` is what ``RoutesProvider`` picks up for the breadcrumb
// trail (Projects / <Project> / Data Manager). Without it the DM tab
// silently dropped out of the trail while Settings / Members showed
// up because they export a ``.title``.
DataManagerPage.title = "Data Manager";
DataManagerPage.path = "/data";
// Explicit displayName so the RoutesProvider-generated breadcrumb
// key is stable ("DataManagerPage") instead of depending on the
// project-specific generated path. The DM context below uses this
// key with addAction/deleteAction to wire the crumb's onClick.
DataManagerPage.displayName = "DataManagerPage";
DataManagerPage.pages = {
  ExportPage,
  ImportModal,
  ProjectMetricsModal,
};
DataManagerPage.context = ({ dmRef }) => {
  const { project } = useProject();
  const { user } = useAuth();
  const [mode, setMode] = useState(dmRef?.mode ?? "explorer");

  const canManageProject =
    !!user &&
    (user.is_staff ||
      user.is_superuser ||
      ["OW", "AD", "MA"].includes(user.active_organization_role) ||
      project?.created_by?.id === user.id);

  const updateCrumbs = (currentMode) => {
    const isExplorer = currentMode === "explorer";

    // "Data Manager" crumb is now contributed by the route itself via
    // ``DataManagerPage.title``. Here we only manage the transient
    // "Labeling" crumb that layers on top when the operator opens
    // the labeling stream inside the DM, and wire the Data Manager
    // crumb's onClick so clicking it from inside the label stream
    // exits labeling and returns to the task list (instead of being
    // a no-op NavLink to the current URL).
    if (isExplorer) {
      deleteCrumb("dm-labeling-crumb");
      deleteAction("DataManagerPage");
    } else {
      addCrumb({ key: "dm-labeling-crumb", title: "Labeling" });
      addAction("DataManagerPage", (e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        dmRef?.store?.closeLabeling?.();
      });
    }
  };

  const showLabelingInstruction = (currentMode) => {
    const isLabelStream = currentMode === "labelstream";
    const { expert_instruction, show_instruction } = project;

    if (isLabelStream && show_instruction && expert_instruction) {
      modal({
        title: "Labeling Instructions",
        body: (
          <div
            className={cn("instructions-body").toClassName()}
            dangerouslySetInnerHTML={{ __html: instructionsToHtml(expert_instruction) }}
          />
        ),
        style: { width: 680 },
      });
    }
  };

  const onDMModeChanged = (currentMode) => {
    setMode(currentMode);
    updateCrumbs(currentMode);
    showLabelingInstruction(currentMode);
  };

  useEffect(() => {
    if (dmRef) {
      dmRef.on("modeChanged", onDMModeChanged);
    }

    return () => {
      dmRef?.off?.("modeChanged", onDMModeChanged);
    };
  }, [dmRef, project]);

  // "Data Manager" crumb is contributed by the route config
  // (DataManagerPage.title). Only clean up the transient labeling
  // crumb + its onClick override when leaving this page so they
  // don't linger on other tabs.
  useEffect(() => {
    return () => {
      deleteCrumb("dm-labeling-crumb");
      deleteAction("DataManagerPage");
    };
  }, []);

  return project && project.id ? (
    <Space size="small">
      {project.expert_instruction && mode !== "explorer" && (
        <Button
          size="small"
          look="outlined"
          onClick={() => {
            modal({
              title: "Instructions",
              body: () => (
                <div
                  className={cn("instructions-body").toClassName()}
                  dangerouslySetInnerHTML={{
                    __html: instructionsToHtml(project.expert_instruction),
                  }}
                />
              ),
            });
          }}
        >
          Instructions
        </Button>
      )}
      {/* Top nav pills — same set rendered on every project-scoped
          page so the operator can flip between Dashboard / Members /
          Data / Settings without going back to the projects list. */}
      {/* Actions an installed extension adds to a project's header. */}
      {mode === "explorer" &&
        (extensions.projectActions ?? []).map((action) => (
          <action.component key={action.id} project={project} canManage={canManageProject} />
        ))}
      <ProjectNav
        projectId={project.id}
        canManage={canManageProject}
        project={project}
        userRole={user?.active_organization_role}
      />
    </Space>
  ) : null;
};
